<template>
  <TransitionRoot
    :show="localOpen"
    as="template"
    @after-leave="query = ''"
    appear
  >
    <Dialog class="relative z-10" @close="updateOpen(false)">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity"
        />
      </TransitionChild>

      <div
        class="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="mx-auto w-full max-w-3xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all"
          >
            <Combobox v-slot="{ activeOption }">
              <div class="relative">
                <MagnifyingGlassIcon
                  class="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <ComboboxInput
                  class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  @change="updateQuery"
                  @blur="query = ''"
                />
              </div>

              <ComboboxOptions
                v-if="filteredPeople.length > 0"
                class="flex transform-gpu flex-col divide-y divide-gray-100 list-none sm:flex-row sm:divide-y-0 sm:divide-x"
                as="div"
                static
                hold
              >
                <div
                  :class="[
                    'max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4',
                    activeOption && 'sm:h-96',
                  ]"
                >
                  <h2
                    v-if="query === ''"
                    class="mb-4 mt-2 text-xs font-semibold text-gray-500"
                  >
                    Recent searches
                  </h2>
                  <div hold class="-mx-2 text-sm text-gray-700">
                    <ComboboxOption
                      v-for="member in filteredPeople"
                      :key="member.id"
                      :value="member"
                      v-slot="{ active }"
                    >
                      <div
                        :class="[
                          'group flex cursor-default select-none items-center rounded-md p-2',
                          active && 'bg-gray-100 text-gray-900',
                        ]"
                      >
                        <img
                          :src="member?.avatarUrl"
                          alt=""
                          class="h-6 w-6 flex-none rounded-full"
                        />
                        <span class="ml-3 flex-auto truncate"
                          >{{ member.firstName }} {{ member.lastName }}</span
                        >
                        <ChevronRightIcon
                          v-if="active"
                          class="ml-3 h-5 w-5 flex-none text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                    </ComboboxOption>
                  </div>
                </div>

                <div
                  v-if="activeOption"
                  class="hidden h-96 w-1/2 flex-none flex-col divide-y divide-gray-100 overflow-y-auto sm:flex"
                >
                  <div class="flex-none p-6 text-center">
                    <h2 class="mt-3 font-semibold text-gray-900">
                      {{ activeOption.firstName + " " + activeOption.lastName }}
                    </h2>
                    <p class="text-sm leading-6 text-gray-500">
                      {{ activeOption.email }}
                    </p>
                  </div>
                  <div class="flex flex-auto flex-col justify-between p-6">
                    <dl
                      class="grid grid-cols-1 gap-x-6 gap-y-3 text-sm text-gray-700"
                    >
                      <dt class="col-end-1 font-semibold text-gray-900">
                        Is user active
                      </dt>
                      <dd>
                        {{ activeOption.isActive ? "Yes" : "No" }}
                      </dd>
                      <dt class="col-end-1 font-semibold text-gray-900">
                        Username
                      </dt>
                      <dd class="truncate">
                        {{ activeOption.username }}
                      </dd>
                      <dt class="col-end-1 font-semibold text-gray-900">
                        Email
                      </dt>
                      <dd class="truncate">
                        <a
                          :href="`mailto:${activeOption.email}`"
                          class="text-indigo-600 underline"
                          >{{ activeOption.email }}</a
                        >
                      </dd>
                    </dl>
                    <button
                      v-if="!withoutRemove"
                      @click="removeUser(activeOption.email)"
                      type="button"
                      class="mt-6 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Remove user from the space
                    </button>
                  </div>
                </div>
              </ComboboxOptions>

              <div
                v-if="query !== '' && filteredPeople.length === 0"
                class="px-6 py-14 text-center text-sm sm:px-14"
              >
                <UsersIcon
                  class="mx-auto h-6 w-6 text-gray-400"
                  aria-hidden="true"
                />
                <p class="mt-4 font-semibold text-gray-900">No people found</p>
                <p class="mt-2 text-gray-500">
                  We couldn’t find anything with that term. Please try again.
                </p>
              </div>
              <div class="ml-[-14px] mr-[-14px] mb-[14px]">
                <AlertMessage
                  :message="alertMessage"
                  :alertType="alertType ? alertType : 'empty'"
                  @close="handleClose"
                />
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  UsersIcon,
} from "@heroicons/vue/24/outline";
import { getMembersBySpaceId, removeMemberFromSpace } from "../services/space";
import AlertMessage from "./AlertMessage.vue";

export default {
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    withoutRemove: {
      type: Boolean,
      default: false,
    },
    spaceId: {
      type: Number,
      default: 0,
    },
    spaceNameId: {
      type: String,
      required: false,
    },
  },
  emits: ["update:open"],
  data() {
    return {
      localOpen: this.open,
      query: "",
      members: [],
      alertMessage: "",
      alertType: "",
    };
  },
  watch: {
    open(newVal) {
      this.localOpen = newVal;
      if (newVal) {
        this.getMembers();
      }
    },
  },
  computed: {
    filteredPeople() {
      return this.query === ""
        ? this.members
        : this.members.filter((person) =>
            person.firstName.toLowerCase().includes(this.query.toLowerCase())
          );
    },
  },
  methods: {
    async removeUser(email) {
      try {
        await removeMemberFromSpace(this.spaceNameId, email);
        const username = this.members.find(
          (member) => member.email === email
        ).username;
        this.alertMessage = `User ${username} has been removed from the space.`;
        this.alertType = "success";
        await this.getMembers();
      } catch {
        this.alertMessage = "Failed to remove user from the space.";
        this.alertType = "dismiss";
      }
    },
    updateOpen(value) {
      this.$emit("update:open", value);
    },
    updateQuery(event) {
      this.query = event.target.value;
    },
    async getMembers() {
      if (!this.spaceId || this.spaceId === 0) {
        this.members = [];
        return;
      }
      try {
        this.members = await getMembersBySpaceId(this.spaceId);
      } catch {
        this.alertMessage = "Failed to get members.";
        this.alertType = "dismiss";
      }
    },
    handleClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
  },
  components: {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    MagnifyingGlassIcon,
    ChevronRightIcon,
    UsersIcon,
    AlertMessage,
  },
};
</script>

<style scoped>
/* Add your styles here */
</style>
